import { useEffect, useState } from 'react'
import { loadRuntimeConfig } from './runtimeConfig'
import { loadContentConfig } from './contentConfig'
import { applyThemeToDocument, getActiveTheme } from './themeCampaigns'
import { hydrateBanners } from '../store/bannerStore'
import { initAnalytics } from './analytics'
import { isPrerendered } from './prerenderEnv'

/**
 * Boot sequence — runs once at app start:
 *   1. Load remote runtime.json (silent on failure)
 *   2. Hydrate banners from runtime + env fallback
 *   3. Apply the active theme campaign to CSS custom props
 *   4. Init analytics (subscribes to consent — scripts only inject if granted)
 *   5. Re-run banner hydration when the tab regains focus (cheap freshness, no polling — Q22)
 */
export function useBootstrap(): { ready: boolean } {
  // Prerendered pages start ready=true so their baked content renders on the
  // server AND on the client's first (hydration) render — the two must match.
  // The boot effect below still runs to load config/theme/analytics; calling
  // setReady(true) again is a harmless no-op. The SPA fallback (empty #root)
  // keeps ready=false, preserving the anti-flag-flash boot gate.
  const [ready, setReady] = useState(isPrerendered)

  // Bumped once runtime.json has loaded. On a PRERENDERED page `ready` already
  // starts true, so setReady(true) below is a no-op that would NOT re-render —
  // leaving flag reads (isFeatureEnabled) stuck on their pre-config defaults.
  // CHAT defaults false, so the chat widget never reappeared on prerendered
  // routes like /da even though runtime.json enables it (it worked on the
  // client-rendered SPA fallback `/`, where ready flips false→true). This
  // separate state forces a re-render when config arrives so post-load flags
  // re-evaluate. The first render stays identical on server + client (false),
  // so hydration still matches.
  const [, setConfigLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false

    void (async () => {
      // Load runtime.json (flags/banners/theme) and content.json (per-salon
      // overrides — PRJT-134) in parallel; both are independent fetches. The
      // single post-load re-render below (setConfigLoaded) covers BOTH, so
      // salon pages re-render with their overrides once content.json lands.
      await Promise.all([loadRuntimeConfig(), loadContentConfig()])
      if (cancelled) return
      hydrateBanners()
      applyThemeToDocument(getActiveTheme())
      initAnalytics()
      setConfigLoaded(true)
      setReady(true)
    })()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState !== 'visible') return
      // Re-fetch runtime so a banner posted while away appears on return.
      void (async () => {
        // We bypass the cached promise by leaning on cache:'no-cache' in the loader.
        // The loader re-uses its single in-flight promise, so we just hydrate from
        // whatever's current. Mutation on next mount cycle is good enough for ops use.
        hydrateBanners()
      })()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  return { ready }
}
